<!-- 「地図から緯度/経度を修正する」モーダル -->
<template>
  <Teleport to="body">
    <div
      id="map-edit-popup"
      class="custom-dialog-overlay js-dialog is-active"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      v-if="isOpen"
    >
      <div class="custom-dialog-content">
        <h3 id="dialog-title" class="dialog-title">
          地図から緯度/経度を修正する
          <img
            src="../../assets/images/icon/button_close.svg"
            alt="閉じる"
            class="close-dialog js-close-dialog-icon"
            @click="(event) => onClickClose(closeModal, event)"
            @touchend.prevent.stop="(event) => onTouchClose(closeModal, event)"
          />
        </h3>
        <div class="dialog-overflow">
          <Form v-slot="{ meta }">
            <div class="dialog-main">
              <div>
                <div>
                  <ul class="edit-map-address">
                    <!--NOTE: leaflet系のz-indexが大きく検索結果が背面になってしまうためstyleを追加-->
                    <li class="map-address" style="z-index: 1010">
                      <div>
                        <label for="" class="label-l">住所検索</label>
                        <InputText
                          :name="'address-input'"
                          type="text"
                          class="input-text"
                          v-model="addressQuery"
                          @keydown.enter.prevent="onSearchAddress"
                          @blur="suggestions = []"
                        />
                        <div
                          class="address-suggestion-box"
                          :class="{ 'is-active': suggestions.length > 0 }"
                        >
                          <p>検索結果</p>
                          <div id="address-suggestions" class="address-suggestion">
                            <ul>
                              <li
                                v-for="(s, i) in suggestions"
                                :key="i"
                                :data-address="s.title"
                                @click="selectSuggestion(s)"
                              >
                                {{ s.title }}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      <button
                        class="button-secondary"
                        id="search-address-btn"
                        type="button"
                        @click="onSearchAddress"
                        :class="{ 'is-disabled': isSearching }"
                      >
                        <span>
                          <img src="../../assets/images/icon/icon_search.svg" alt="" />
                        </span>
                        位置を検索
                      </button>
                    </li>
                    <li class="edit-map-lat-lon">
                      <div>
                        <label for="" class="label-l">
                          緯度 <span class="required-mark">※必須</span>
                        </label>
                        <NumberField
                          name="latitude"
                          v-model="modalLat"
                          :rules="`required|between:${MIN_LATITUDE},${MAX_LATITUDE},7`"
                          :decimals="7"
                          inputClass="input-text num"
                        />
                        <ErrorMessageBox name="latitude" />
                      </div>
                      <div>
                        <label for="" class="label-l">
                          経度 <span class="required-mark">※必須</span>
                        </label>
                        <NumberField
                          name="longitude"
                          v-model="modalLon"
                          :rules="`required|between:${MIN_LONGITUDE},${MAX_LONGITUDE},7`"
                          :decimals="7"
                          inputClass="input-text num"
                        />
                        <ErrorMessageBox name="longitude" />
                      </div>
                      <button class="button-secondary" @click="applyToMap" type="button">
                        <span>
                          <img src="../../assets/images/icon/icon_update.svg" alt="" />
                        </span>
                        地図を更新する
                      </button>
                    </li>
                  </ul>
                  <GsiMap
                    ref="gsiMap"
                    mode="single"
                    :lat="mapLat"
                    :lon="mapLon"
                    :mapType="mapModalStore.mapType"
                    :isModal="true"
                    :markerContext="mapContext"
                    :markerLabel="mapModalStore.markerLabel"
                    :singleMarker="{
                      routeWind: mapModalStore.routeWind,
                      upperWind: mapModalStore.upperWind,
                    }"
                    @update:lat="modalLat = $event"
                    @update:lon="modalLon = $event"
                  />
                </div>
                <div class="dialog-action-button">
                  <button
                    class="button-secondary"
                    id="close-map-edit-popup"
                    @click="(event) => onClickClose(cancel, event)"
                    @touchend.prevent.stop="(event) => onTouchClose(cancel, event)"
                    type="button"
                  >
                    <span>
                      <img src="../../assets/images/icon/icon_closeX_black.svg" alt="" />
                    </span>
                    キャンセル
                  </button>
                  <button
                    class="button-primary"
                    @click="(event) => onClickClose(apply, event)"
                    @touchend.prevent.stop="(event) => onTouchClose(apply, event)"
                    :class="{ 'is-disabled': !meta.valid }"
                    type="button"
                  >
                    この地点を反映する
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { Form } from 'vee-validate'
import NumberField from '@/components/form/NumberField.vue'
import InputText from '@/components/form/InputText.vue'
import ErrorMessageBox from '@/components/form/ErrorMessageBox.vue'
import GsiMap from '@/components/map/GsiMap.vue'
import { useMapModalLogic } from '@/composables/useMapModal'
import type { MapType } from '@/types/map'
import { MIN_LATITUDE, MAX_LATITUDE, MIN_LONGITUDE, MAX_LONGITUDE } from '@/constants'

const props = defineProps<{
  isOpen: boolean
  lat?: number | null
  lon?: number | null
  modelValue?: string
  mapType: MapType
  mapContext: 'flight-area' | 'route-point'
  markerLabel?: string
}>()

const emit = defineEmits<{
  (e: 'apply', payload: { lat: number; lon: number }): void
  (e: 'update:mapType', value: MapType): void
  (e: 'cancel'): void
}>()

const {
  mapModalStore,
  modalLat,
  modalLon,
  mapLat,
  mapLon,
  gsiMap,
  addressQuery,
  suggestions,
  isSearching,
  onSearchAddress,
  selectSuggestion,
  applyToMap,
} = useMapModalLogic(props)

let lastTouchCloseAt = 0

// 「この地点を反映する」ボタン押下
function apply(event?: Event) {
  event?.preventDefault()
  event?.stopPropagation()

  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }

  const pin = gsiMap.value?.getPinPosition?.()
  emit('apply', { lat: pin.lat, lon: pin.lon })
  mapModalStore.close()

  requestAnimationFrame(() => {
    document.body.focus()
  })
}

// iPhone Safari でモーダルのボタンをタップした場合、下の画面のボタンにフォーカスが当たってしまうため
// touchend 後に発生する click は無視
function onTouchClose(action: (event?: Event) => void, event?: Event) {
  lastTouchCloseAt = Date.now()
  action(event)
}
function onClickClose(action: (event?: Event) => void, event?: Event) {
  if (Date.now() - lastTouchCloseAt < 700) {
    return
  }

  action(event)
}

function dismissModal(action: () => void, event?: Event) {
  event?.preventDefault()
  event?.stopPropagation()

  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }

  action()

  requestAnimationFrame(() => {
    document.body.focus()
  })
}

function cancel(event?: Event) {
  dismissModal(() => mapModalStore.cancel(), event)
}

function closeModal(event?: Event) {
  dismissModal(() => mapModalStore.close(), event)
}
</script>
